// 実績システムのユーティリティ関数

// 実績の定義
export const ACHIEVEMENTS = {
    FIRST_QUIZ: {
        id: 'first_quiz',
        name: '初回クイズ',
        description: '初めてクイズを完了しました',
        icon: '🎉',
        type: 'quiz_completion'
    },
    PERFECT_SCORE: {
        id: 'perfect_score',
        name: '完璧主義者',
        description: '満点を取得しました',
        icon: '💯',
        type: 'score'
    },
    QUIZ_MASTER_10: {
        id: 'quiz_master_10',
        name: 'クイズマスター',
        description: '10回クイズを完了しました',
        icon: '🏆',
        type: 'quiz_completion'
    },
    QUIZ_MASTER_50: {
        id: 'quiz_master_50',
        name: 'クイズエキスパート',
        description: '50回クイズを完了しました',
        icon: '👑',
        type: 'quiz_completion'
    },
    STREAK_5: {
        id: 'streak_5',
        name: '連続正解',
        description: '5問連続で正解しました',
        icon: '🔥',
        type: 'streak'
    },
    STREAK_10: {
        id: 'streak_10',
        name: '完璧ストリーク',
        description: '10問連続で正解しました',
        icon: '⭐',
        type: 'streak'
    },
    NOUN_MASTER: {
        id: 'noun_master',
        name: '名詞マスター',
        description: '名詞カテゴリのすべての難易度を完了しました',
        icon: '📚',
        type: 'category'
    },
    VERB_MASTER: {
        id: 'verb_master',
        name: '動詞マスター',
        description: '動詞カテゴリのすべての難易度を完了しました',
        icon: '🏃',
        type: 'category'
    },
    HIGH_SCORER: {
        id: 'high_scorer',
        name: '高得点者',
        description: '80%以上の正答率を10回達成しました',
        icon: '🎯',
        type: 'score'
    },
    REVIEWER: {
        id: 'reviewer',
        name: '復習王',
        description: '復習モードを10回完了しました',
        icon: '📖',
        type: 'review'
    }
};

// 実績データの初期化
export const initializeAchievements = () => {
    const achievements = localStorage.getItem('achievements');
    if (!achievements) {
        const initialAchievements = Object.keys(ACHIEVEMENTS).reduce((acc, key) => {
            acc[ACHIEVEMENTS[key].id] = {
                unlocked: false,
                unlockedAt: null,
                progress: 0
            };
            return acc;
        }, {});
        localStorage.setItem('achievements', JSON.stringify(initialAchievements));
    }
};

// 実績データの取得
export const getAchievements = () => {
    initializeAchievements();
    return JSON.parse(localStorage.getItem('achievements') || '{}');
};

// 実績データの保存
export const saveAchievements = (achievements) => {
    localStorage.setItem('achievements', JSON.stringify(achievements));
};

// 実績の解除
export const unlockAchievement = (achievementId) => {
    const achievements = getAchievements();
    if (!achievements[achievementId]?.unlocked) {
        achievements[achievementId] = {
            ...achievements[achievementId],
            unlocked: true,
            unlockedAt: new Date().toISOString()
        };
        saveAchievements(achievements);
        return true; // 新しく解除された
    }
    return false; // 既に解除済み
};

// 実績の進捗更新
export const updateAchievementProgress = (achievementId, progress) => {
    const achievements = getAchievements();
    if (achievements[achievementId] && !achievements[achievementId].unlocked) {
        achievements[achievementId].progress = progress;
        saveAchievements(achievements);
    }
};

// 統計データの取得
export const getStats = () => {
    const stats = localStorage.getItem('quizStats');
    if (!stats) {
        const initialStats = {
            totalQuizzes: 0,
            totalQuestions: 0,
            totalCorrect: 0,
            perfectScores: 0,
            highScores: 0, // 80%以上
            maxStreak: 0,
            currentStreak: 0,
            reviewSessions: 0,
            categoriesCompleted: {
                nouns: [],
                verbs: []
            }
        };
        localStorage.setItem('quizStats', JSON.stringify(initialStats));
        return initialStats;
    }
    return JSON.parse(stats);
};

// 統計データの保存
export const saveStats = (stats) => {
    localStorage.setItem('quizStats', JSON.stringify(stats));
};

// クイズ完了時の統計更新
export const updateQuizStats = (score, total, category, difficulty) => {
    const stats = getStats();
    
    stats.totalQuizzes++;
    stats.totalQuestions += total;
    stats.totalCorrect += score;
    
    const percentage = (score / total) * 100;
    
    if (percentage === 100) {
        stats.perfectScores++;
    }
    
    if (percentage >= 80) {
        stats.highScores++;
    }
    
    // カテゴリ完了の記録
    if (category && difficulty) {
        const categoryKey = category === 'noun' ? 'nouns' : 'verbs';
        if (!stats.categoriesCompleted[categoryKey].includes(difficulty)) {
            stats.categoriesCompleted[categoryKey].push(difficulty);
        }
    }
    
    saveStats(stats);
};

// 復習セッション完了時の統計更新
export const updateReviewStats = () => {
    const stats = getStats();
    stats.reviewSessions++;
    saveStats(stats);
};

// 連続正解の更新
export const updateStreak = (correct) => {
    const stats = getStats();
    
    if (correct) {
        stats.currentStreak++;
        stats.maxStreak = Math.max(stats.maxStreak, stats.currentStreak);
    } else {
        stats.currentStreak = 0;
    }
    
    saveStats(stats);
};

// 解除された実績の取得
export const getUnlockedAchievements = () => {
    const achievements = getAchievements();
    return Object.keys(achievements)
        .filter(id => achievements[id].unlocked)
        .map(id => ({
            ...ACHIEVEMENTS[Object.keys(ACHIEVEMENTS).find(key => ACHIEVEMENTS[key].id === id)],
            unlockedAt: achievements[id].unlockedAt
        }));
};

// 未解除の実績の取得
export const getLockedAchievements = () => {
    const achievements = getAchievements();
    return Object.keys(achievements)
        .filter(id => !achievements[id].unlocked)
        .map(id => ({
            ...ACHIEVEMENTS[Object.keys(ACHIEVEMENTS).find(key => ACHIEVEMENTS[key].id === id)],
            progress: achievements[id].progress
        }));
};

// 実績チェック関数
export const checkAchievements = () => {
    const stats = getStats();
    const newlyUnlocked = [];
    
    // 初回クイズ完了
    if (stats.totalQuizzes >= 1) {
        if (unlockAchievement('first_quiz')) {
            newlyUnlocked.push(ACHIEVEMENTS.FIRST_QUIZ);
        }
    }
    
    // 満点獲得
    if (stats.perfectScores >= 1) {
        if (unlockAchievement('perfect_score')) {
            newlyUnlocked.push(ACHIEVEMENTS.PERFECT_SCORE);
        }
    }
    
    // クイズマスター (10回)
    if (stats.totalQuizzes >= 10) {
        if (unlockAchievement('quiz_master_10')) {
            newlyUnlocked.push(ACHIEVEMENTS.QUIZ_MASTER_10);
        }
    }
    
    // クイズエキスパート (50回)
    if (stats.totalQuizzes >= 50) {
        if (unlockAchievement('quiz_master_50')) {
            newlyUnlocked.push(ACHIEVEMENTS.QUIZ_MASTER_50);
        }
    }
    
    // 連続正解 (5問)
    if (stats.maxStreak >= 5) {
        if (unlockAchievement('streak_5')) {
            newlyUnlocked.push(ACHIEVEMENTS.STREAK_5);
        }
    }
    
    // 完璧ストリーク (10問)
    if (stats.maxStreak >= 10) {
        if (unlockAchievement('streak_10')) {
            newlyUnlocked.push(ACHIEVEMENTS.STREAK_10);
        }
    }
    
    // 高得点者 (80%以上を10回)
    if (stats.highScores >= 10) {
        if (unlockAchievement('high_scorer')) {
            newlyUnlocked.push(ACHIEVEMENTS.HIGH_SCORER);
        }
    }
    
    // 復習王 (復習モード10回)
    if (stats.reviewSessions >= 10) {
        if (unlockAchievement('reviewer')) {
            newlyUnlocked.push(ACHIEVEMENTS.REVIEWER);
        }
    }
    
    // 名詞マスター (すべての難易度完了)
    if (stats.categoriesCompleted.nouns.length >= 3) { // 難易度が3つあると仮定
        if (unlockAchievement('noun_master')) {
            newlyUnlocked.push(ACHIEVEMENTS.NOUN_MASTER);
        }
    }
    
    // 動詞マスター (すべての難易度完了)
    if (stats.categoriesCompleted.verbs.length >= 3) { // 難易度が3つあると仮定
        if (unlockAchievement('verb_master')) {
            newlyUnlocked.push(ACHIEVEMENTS.VERB_MASTER);
        }
    }
    
    return newlyUnlocked;
};